import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { UserPlus, Copy, Check, Loader2 } from 'lucide-react';
import { xamplinApi, type InviteScope } from '@/lib/api/xamplin';
import { toast } from '@/components/ui/Toast';

/**
 * Mint a passwordless scoped intake invite (build-v2 F7) and surface a
 * copyable magic-link. Drop next to a page header for the target entity.
 */
export function IntakeInviteButton({
  scope,
  targetId,
  role,
  label = 'Invitar colaborador',
}: {
  scope: InviteScope;
  targetId: number;
  role?: string;
  label?: string;
}) {
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mintM = useMutation({
    mutationFn: () => xamplinApi.intake.create({ scope, targetId, role }),
    onSuccess: (res) => {
      const url = `${window.location.origin}${res.path}`;
      setLink(url);
      void navigator.clipboard?.writeText(url).then(
        () => {
          setCopied(true);
          toast({ tone: 'success', title: 'Enlace copiado al portapapeles' });
        },
        () => toast({ tone: 'info', title: 'Enlace generado' }),
      );
    },
    onError: () => toast({ tone: 'error', title: 'No se pudo generar el enlace' }),
  });

  if (link) {
    return (
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(link);
          setCopied(true);
          toast({ tone: 'success', title: 'Enlace copiado' });
        }}
        className="flex items-center gap-2 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-1.5 text-xs font-bold text-[var(--color-xk-lavender)] hover:border-[var(--color-xk-accent)]"
        title={link}
      >
        {copied ? <Check className="h-3.5 w-3.5 text-[var(--color-success)]" /> : <Copy className="h-3.5 w-3.5" />}
        Copiar enlace de intake
      </button>
    );
  }

  return (
    <button
      type="button"
      disabled={mintM.isPending}
      onClick={() => mintM.mutate()}
      className="flex items-center gap-2 rounded-lg border border-[var(--color-xk-border)] px-3 py-1.5 text-xs font-bold text-[var(--color-xk-text-muted)] hover:border-[var(--color-xk-accent)] hover:text-[var(--color-xk-lavender)] disabled:opacity-50"
    >
      {mintM.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}
